import _ from 'lodash';
import moment from 'moment';
import store from './../../store';
import { standardFunctions } from '@/helpers/standardFunctions';
import { helperFns } from '@/helpers';
import { TranslationService } from '@/services/TranslationService';
import { RolesService } from '@/services/RolesService';
import { FeatureFlagsService } from '@/services/FeatureFlagsService';

export default function resolveFormulaValue(parent: any, formulaString: string, variables?: { _item?: any, _variable?: any, _object?: any }, index?: number): any {
  // all possible functions should be here
  const availableFunctions: any = {
    '': ({ result }: { result: any }) => result,
    'SUM': ({ args }: { args: string[] }) => {
      return (args.reduce((a, b) => Number(a) + Number(b), 0))
    },
    'MULTIPLY': ({ args }: { args: string[] }) => {
      return (args.reduce((a, b) => Number(a) * Number(b), 1))
    },
    'SUBTRACT': ({ args: [argument1, argument2] }: { args: string[] }) => {
      return _.round((Number(argument1) - Number(argument2)),5)
    },
    'DIVIDE': ({ args: [argument1, argument2] }: { args: string[] }) => {
      return (Number(argument1) / Number(argument2))
    },
    'CONCAT': ({ args }: { args: string[] }) => {
      return args.map(data => data).join('')
    },
    'LENGTH': ({ args: [arrray] }: { args: string[] }) => {
      return arrray.length || 0;
    },
    'NUMBER': ({ args: [numberInString] }: { args: string[] }) => {
      try {
        return Number(numberInString);
      } catch (error) {
        return numberInString;
      }
    },
    'TO_ARRAY': ({ args: [objectToConvert] }: { args: any[] }) => {
      return _.map(objectToConvert, (value, _key) => ({ _key, ...value }))
    },
    'DEEP_EQUAL':({ args: [argument1, argument2] }: { args: string[] }) => {
      return _.isEqual(argument1, argument2);
    },
    'LOGICAL': ({ args: [argument1, operator, argument2] }: { args: string[] }) => {
      return [argument1,argument2].includes('undefined') || [argument1,argument2].includes('') ? false : eval(`${argument1} ${operator} ${argument2}`);
    },
    'INCLUDE_ARRAY': ({ args: [inputArray, matchedString ] }: { args: any[] }) => {
      return _.includes(inputArray, matchedString);
    },
    'NOT': ({ args: [argument] }: { args: any[] }) => {
      return argument == 'undefined' || argument == '' ? true : !argument;
    },
    'TIME_DIFF': ({ args: [startTimeString, endTimeString] }: { args: string[] }) => {
      const startTime = moment(startTimeString, 'HH:mm');
      const endTime = moment(endTimeString, 'HH:mm');
      const timeDiffInHrs = _.round(moment.duration(endTime.diff(startTime)).asHours(), 2);
      return `${isNaN(timeDiffInHrs) ? '' : timeDiffInHrs}`;
    },
    'ADD_DAYS': ({ args: [ initialDate, daysToAdd, type ] }: { args: string[]}) => {
      const date = (initialDate === 'TODAYS_DATE') ? new Date() : new Date(initialDate);
      const newDate = type == 'month' ? moment(date).add(daysToAdd, 'month').toISOString() : moment(date).add(daysToAdd, 'days').toISOString();
      return newDate;
    },
    'GET_GOOD': ({ args: [ goodId, goodsDataKey ] }: { args: string[] }) => {
      const parsedGoodId = Number(goodId) ? Number(goodId) : goodId;
      const selectedGood = parent.getGoodFromId(parsedGoodId);
      return selectedGood ? _.get(selectedGood, goodsDataKey, selectedGood) : '';
    },
    'MATCH_ROLE': ({ args: [ expectedRole ] }: { args: string[] }) => {
      const currentRole = store.state.user.role;
      return currentRole.includes(expectedRole) || store.getters.roleMatches(expectedRole);
    },
    'MATCH_DEPARTMENT': ({ args: [ expectedDepartment ] }: { args: string[] }) => {
      const currentRoles: string[] = store.state.user.role;
      const staticRoleNames = currentRoles.map((role: string) => RolesService.getStaticRoleName(role));
      return staticRoleNames.some((role: string | undefined) => !!role && role.startsWith(expectedDepartment));
    },
    'ARRAY_SUM': ({args: [ computation, itemsArrayAddress ] }: { args: string[]}) => {
      const itemArray: any[] = computeString(parent, itemsArrayAddress, variables) || [];
      const arraySum = itemArray.reduce((accumulator, currentItem) => {
        return accumulator + Number(computeString(parent, computation, { _item: currentItem }));
      }, 0);
      return _.round(arraySum, 5);
    },
    'KEYED_ARRAY_SUM': ({args: [ computationFnRef, itemsArrayAddress, itemsArrayKey, variableArrayAddress, variableArrayKey ] }: { args: any[]}, allFunctions: any[]) => {
      const itemArray: any[] = computeString(parent, itemsArrayAddress, variables) || [];
      const variableArray: any[] = variableArrayAddress ? computeString(parent, variableArrayAddress, variables) : [];
      const combinedArray = _.map(itemArray, function(currentItem) {
        return _.assign(currentItem, _.find(variableArray, {
          [variableArrayKey]: currentItem[itemsArrayKey]
        }));
      });
      return combinedArray.reduce((accumulator, currentItem) => {
        const beforeCompute = (parsedFn: any) => {
          const args = parsedFn.args.map((currentArg: any) => {
            if(typeof(currentArg) === 'string') {
              currentArg = currentArg.replace(/{{__arrayItem\.([^{^}]*)}}/g, (match: string, subString: string) => {
                const resolvedValue = _.get(currentItem, subString) || 0;
                return JSON.stringify(resolvedValue)
            })
            } else {
              currentArg = computeSingleFunction(allFunctions[currentArg.ref], allFunctions, beforeCompute)
            }
            return currentArg;
          })
          return { ...parsedFn, args, delayCompute: false };
        }
        return accumulator + Number(computeSingleFunction(allFunctions[computationFnRef.ref], allFunctions, beforeCompute));
      }, 0);
    },
    'CONDITIONAL': ({args: [ logicalCondition, successValue, failureValue ] }: { args: any[]}) => {
      return logicalCondition ? successValue : failureValue
    },
    'STANDARD_FUNCTIONS': ({ args: [ fnName, ...fnArguments ] }: { args: any[] }) => {
      return standardFunctions[fnName](...fnArguments);
    },
    'TRANSLATE': ({ args: [ text, languageOption ] }: { args: string[] }) => {
      if(!text) return '';
      return TranslationService.translate(text, languageOption);
    },
    'HELPER_FN': ({ args: [ fnName, ...fnArguments ] }: { args: any[]}) => {
      return helperFns[fnName](...fnArguments);
    },
    'FEATURE_FLAG': ({ args: [ featureName ] }: { args: string[]}) => {
      return FeatureFlagsService.checkStatus(featureName);
    } 
  }
  function computeSingleFunction(parsedFunction: any, allFunctions: any[], beforeCompute?: any) {
    if (beforeCompute) {
      parsedFunction = beforeCompute(parsedFunction)
    }
    // go through all args that are linked to other functions and get their results
    parsedFunction.args = parsedFunction.args.map((arg: any) => {
      if (arg.ref !== undefined && allFunctions[arg.ref].result !== undefined) {
        return allFunctions[arg.ref].result;
      }
      return parsedFunction.delayCompute ? arg : computeString(parent, `${arg}`, variables, index)
    })
    return availableFunctions[parsedFunction.name](parsedFunction, allFunctions);
  }

  function computeString(parent: any, templateString: string, variables?: { _item?: any, _variable?: any, _object?: any }, index?: number) {
    let resolvedString = templateString.replace(/{{__index}}/g, `${index}`)
    resolvedString = resolvedString.replace(/{{TODAYS_DATE}}/g, new Date().toISOString());
    resolvedString = resolvedString.replace(/{{YEAR_START}}/g, new Date(new Date().getFullYear(), 0, 1, 12).toISOString());
    resolvedString = resolvedString.replace(/{{MONTH_START}}/g, moment().startOf('month').add(1, 'day').toISOString());
    resolvedString = resolvedString.replace(/{{MONTH_END}}/g, moment().endOf('month').toISOString());
    resolvedString = resolvedString.replace(/{{CURRENT_MONTH_NAME}}/g, moment().format('MMMM'));
    resolvedString = resolvedString.replace(/{{CURRENT_YEAR}}/g, moment().format('YYYY'));
    if(resolvedString.includes('{{FORECAST_YEAR}}')) {
      const currentYear = moment().year();
      const forecastYear = (moment().month() < 8) ? currentYear : currentYear + 1;
      resolvedString = resolvedString.replace(/{{FORECAST_YEAR}}/g, `${forecastYear}`);
    }
    const regex = /{{([^}^{]*)}}/g;
    let endlessLoopAvoidance = 0;
    while(regex.test(resolvedString) && endlessLoopAvoidance < 10) {
      endlessLoopAvoidance++;
      resolvedString = resolvedString.replace(regex, (match, p1) => {
        let returnedValue;
        if(p1.startsWith('__params.')) {
          returnedValue = parent.variables.params[p1.split('__params.')[1]];
        } else if(p1.startsWith('__store.')) {
          const storeVariable = p1.split('__store.')[1];
          returnedValue = _.get(store, `state.${storeVariable}`, '');
        } else if(p1.startsWith('__query.')) {
          returnedValue = parent.variables.query[p1.split('__query.')[1]];
        } else if(p1.startsWith('__selectedObject')) {
          const value = p1.split('__selectedObject.')[1] ? _.get(variables && variables._object, p1.split('__selectedObject.')[1]) : variables && variables._object; 
          returnedValue = typeof(value) === 'string' ? value : JSON.stringify(value);
        } else if(p1.startsWith('__item')) {
          const value = p1.split('__item.')[1] ? _.get(variables && variables._item, p1.split('__item.')[1]) : variables && variables._item;
          if(!value) {
            returnedValue = '0';
          } else if(/[0-9]{4}-[0-9]{2}-[0-9]{2}/g.test(value)) {
            returnedValue = value;
          } else {
            returnedValue = typeof(value) === 'string' ? value : JSON.stringify(value);
          }
        } else if(p1.startsWith('__variable')) {
          const value = p1.split('__variable.')[1] ? _.get(variables && variables._variable, p1.split('__variable.')[1]) : variables && variables._variable;
          if(!value) {
            returnedValue = '0';
          } else if(/[0-9]{4}-[0-9]{2}-[0-9]{2}/g.test(value)) {
            returnedValue = value;
          } else {
            returnedValue = typeof(value) === 'string' ? value : JSON.stringify(value);
          }
        } else if(p1.startsWith('__response.')) {
          returnedValue = _.get(parent.variables.response, p1.split('__response.')[1]);
        } else if(p1.startsWith('formData') || p1.startsWith('fetchedData')) {
          let value = parent._getVariable(p1);
          value = Number.isInteger(value) || typeof value == 'boolean' ? `${value}`: value || '';
          returnedValue = typeof(value) === 'string' ? value : JSON.stringify(value);
        } else {
          returnedValue = p1;
        }
        if(/[0-9]{4}-[0-9]{2}-[0-9]{2}/g.test(returnedValue)) return returnedValue;
        try {
          const evaluatedValue = eval(returnedValue);
          if(typeof evaluatedValue == 'object') return returnedValue;
          const isBoolean = typeof evaluatedValue === 'boolean';
          if (!isNaN(evaluatedValue) && isBoolean) {
            return evaluatedValue;
          } else {
            const roundedValue = _.floor(evaluatedValue, 5);
            return isNaN(roundedValue) ? returnedValue : roundedValue;
          }
        } catch (error) {
          return returnedValue;
        }
      });
    }
    try {
      return JSON.parse(resolvedString)
    } catch(err) {
      return resolvedString == 'undefined' ? undefined : resolvedString;
    }
  }

  const computeAllFunctions = (allFunctions: any) => {
    allFunctions.forEach((parsedFunction: any) => {
      if(!parsedFunction.delayCompute || ['KEYED_ARRAY_SUM', 'ARRAY_SUM'].includes(parsedFunction.name)) {
        parsedFunction.result = computeSingleFunction(parsedFunction, allFunctions)
      }
    })
    return allFunctions[allFunctions.length - 1].result
  }
  const parseString = (str: string) => {
    const tempFunctions = [];
    const functions = [];
    const paranthesesArray = [];
    const quotesArray = [];
    const bracesArray = [];
    for (let index = 0; index < str.length; index++) {
      if(str[index] === '"') {
        if(quotesArray.length > 0) {
          quotesArray.pop()
        } else {
          quotesArray.push(str[index]);
        }
      }
      if(str[index] === '[') bracesArray.push(str[index]);
      if(str[index] === ']') bracesArray.pop();
      const currentTempFunction = tempFunctions[tempFunctions.length - 1];
      if(str[index] === '(') {
        if (str.substring(index - 2, index + 1) === 'fn(') {
          const newFunction: { toLink: any[], args: any[], startIndex: number, lastArgIndex: number, name?: string, avoidNext?: boolean, delayCompute?: boolean } = {
            toLink: [],
            args: [],
            startIndex: index,
            lastArgIndex: index + 1
          };
          if (currentTempFunction != null) {
            const refObject = {
              ref: 'N/A',
            };
            newFunction.toLink.push(refObject)
            currentTempFunction.args.push(refObject)
            currentTempFunction.avoidNext = true;
            if(currentTempFunction.delayCompute) {
              newFunction.delayCompute = true;
            }
          }
          tempFunctions.push(newFunction)   
        } else {
          paranthesesArray.push(str[index]);
        }
      }
      if (str[index] === ",") {
        if(quotesArray.length === 0 && bracesArray.length === 0) {
          const currentArg = str.substring(currentTempFunction.lastArgIndex, index);
          if (!currentTempFunction.name) {
            currentTempFunction.name = currentArg;
            if(['KEYED_ARRAY_SUM', 'ARRAY_SUM'].includes(currentTempFunction.name)) {
              currentTempFunction.delayCompute = true;
            }
          } else if (!currentTempFunction.avoidNext) {
            currentTempFunction.args.push(currentArg)
          }
          currentTempFunction.avoidNext = false
          currentTempFunction.lastArgIndex = index + 1;
        }
      }
      if (str[index] === ")") {
        if(paranthesesArray.length > 0) {
          paranthesesArray.pop();
        } else {
          if (!currentTempFunction.avoidNext) {
            const currentArg = str.substring(currentTempFunction.lastArgIndex, index);
            currentTempFunction.args.push(currentArg)
          }
          currentTempFunction.avoidNext = false
          const lastTempFunction = tempFunctions.pop()
          if(lastTempFunction) {
            functions.push(lastTempFunction);
          }
          functions[functions.length - 1].toLink.forEach(refObject => {
            refObject.ref = functions.length - 1;
          })
        }
      }
    }
    if (functions.length == 0) {
      functions.push({
        result: computeString(parent, str, variables, index),
        name: '',
        args: []
      })
    }
    return functions;
  }
  const parseAndComputeString = (str: string) => {
    if(str) {
      const functionsArray = parseString(str);
      return computeAllFunctions(functionsArray);
    }
    return '';
  }
  return parseAndComputeString(formulaString)
}